// src/components/SuperadminPanel.js
import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Chip, Alert, CircularProgress, Divider } from '@mui/material';
import AdminUsersSettings from './AdminUsersSettings';
import { useTranslation } from '../i18n/i18n';
import API_ENDPOINTS from '../config/apiConfig';

function SubscriptionManager() {
  const { t } = useTranslation();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.SUBSCRIPTION_STATUS, { credentials: 'include' });
        if (res.ok) setSub(await res.json());
        else setSub({ active: false, status: 'none' });
      } catch {
        setSub({ active: false, status: 'none' });
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleCheckout = async () => {
    setActionLoading(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SUBSCRIPTION_CHECKOUT, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.SUBSCRIPTION_PORTAL, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create portal session');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setActionLoading(false);
    }
  };

  const statusColors = {
    active: 'success',
    trialing: 'info',
    past_due: 'warning',
    canceled: 'error',
    unpaid: 'error',
    incomplete: 'warning',
    incomplete_expired: 'error',
    none: 'default',
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const status = sub?.status || 'none';
  const isActive = sub?.active === true;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {t('subscription_status') || 'Subscription Status'}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('subscription_manage_subtitle') || 'Manage your Babordo platform subscription and billing.'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('subscription_status') || 'Status'}</Typography>
          <Box>
            <Chip
              label={t(`subscription_${status}`) || status}
              color={statusColors[status] || 'default'}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('subscription_plan') || 'Plan'}</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('subscription_babordo_plan') || 'Babordo Platform'}
          </Typography>
        </Box>
        {sub?.trial_end && (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('subscription_trial_ends') || 'Trial Ends'}</Typography>
            <Typography variant="body1">{formatDate(sub.trial_end)}</Typography>
          </Box>
        )}
        {sub?.current_period_end && (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {isActive ? (t('subscription_next_billing') || 'Next Billing') : (t('subscription_expires') || 'Expires')}
            </Typography>
            <Typography variant="body1">{formatDate(sub.current_period_end)}</Typography>
          </Box>
        )}
        {sub?.current_period_start && (
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('subscription_started') || 'Started'}</Typography>
            <Typography variant="body1">{formatDate(sub.current_period_start)}</Typography>
          </Box>
        )}
      </Box>

      {sub?.in_grace_period && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('subscription_grace_period') || 'Grace Period'} — {sub.grace_period_days_remaining} {t('subscription_grace_period') || 'day(s) remaining'}
        </Alert>
      )}

      {sub?.payment_failed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('subscription_past_due') || 'Payment overdue. Please update your payment method.'}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {!isActive && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleCheckout}
          disabled={actionLoading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {t('subscription_subscribe') || 'Subscribe Now'}
        </Button>
      )}

      {isActive && (
        <Button
          variant="outlined"
          onClick={handlePortal}
          disabled={actionLoading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {t('subscription_manage') || 'Manage Subscription'}
        </Button>
      )}

      {!isActive && status !== 'none' && sub?.customer_id && (
        <Button
          variant="outlined"
          onClick={handlePortal}
          disabled={actionLoading}
          sx={{ ml: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {t('subscription_update_payment') || 'Update Payment Method'}
        </Button>
      )}
    </Box>
  );
}

export default function SuperadminPanel() {
  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('superadmin_panel') || 'Superadmin'}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          {t('superadmin_subtitle') || 'Manage platform administrators and their permissions.'}
        </Typography>
      </Box>

      {/* Subscription Management Section */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', mb: 3 }}>
        <SubscriptionManager />
      </Paper>

      {/* Admin Management Section */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <AdminUsersSettings />
      </Paper>
    </Box>
  );
}
